// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, Loading, ScrollView, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useNavigationDOM, useQuery, useResponsive, useToast} from '@supermove/hooks';
import {Project, Job, ProjectModel, OrganizationModel} from '@supermove/models';
import {SupermoveNavigatorType} from '@supermove/navigation/src/NavigationTypes';
import {colors} from '@supermove/styles';

// App
import Button from '@shared/design/components/Button';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import Callout from '@shared/design/components/Callout';
import DropdownInput from '@shared/design/components/DropdownInput';
import FieldInput from '@shared/design/components/Field/FieldInput';
import ActionPanel from '@shared/design/components/Panel/ActionPanel';
import ErrorToast from '@shared/design/components/Toast/ErrorToast';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import OrganizationKind from '@shared/modules/Organization/enums/OrganizationKind';
import TransferProjectToOrganizationForm from '@shared/modules/Project/forms/TransferProjectToOrganizationForm';
import UpdateJobTypeForJobForm from '@shared/modules/Project/forms/UpdateJobTypeForJobForm';
import useTransferProjectMutation from '@shared/modules/Project/hooks/useTransferProjectMutation';
import OfficeHeader, {OfficeHeaderBuilder} from 'modules/App/components/OfficeHeader';
import SidebarPageV2 from 'modules/App/components/SidebarPageV2';
import SkeletonLoader from 'modules/App/components/SkeletonLoader';
import EditJobTypesContent from 'modules/Project/V2/Edit/components/EditJobTypesContent';
import TransferProjectTypeContent from 'modules/Project/V2/Edit/components/TransferProjectTypeContent';

const PageContainer = Styled.View`
  flex: 1;
`;

const ContentScrollContainer = ({children}: any) => {
  const responsive = useResponsive();

  return (
    <ScrollView
      style={{
        flex: 1,
        backgroundColor: responsive.desktop ? colors.gray.background : colors.white,
      }}
      contentContainerStyle={{
        alignItems: responsive.desktop ? 'center' : 'stretch',
        paddingHorizontal: responsive.desktop ? 24 : 0,
        paddingVertical: responsive.desktop ? 24 : 0,
      }}
    >
      {children}
    </ScrollView>
  );
};

const LoadingComponent = ({navigator}: {navigator: SupermoveNavigatorType}) => {
  return (
    <SidebarPageV2 selected={'moves'}>
      <PageContainer>
        <SkeletonLoader navigator={navigator} />
      </PageContainer>
    </SidebarPageV2>
  );
};

const PageHeader = ({
  project,
  handleSubmit,
  submitting,
  onClose,
}: {
  project: ProjectModel;
  handleSubmit: any;
  submitting: boolean;
  onClose: () => void;
}) => {
  return (
    <OfficeHeader>
      <OfficeHeaderBuilder.Content style={{flex: 1, padding: 24}}>
        <TertiaryButton onPress={onClose}>
          <Icon source={Icon.XmarkLarge} size={22} color={colors.gray.primary} />
        </TertiaryButton>
        <Space width={16} />
        <OfficeHeaderBuilder.Title>{`Transfer ${Project.getName(project)}`}</OfficeHeaderBuilder.Title>
        <Space style={{flex: 1}} />
        <Button
          text={'Save'}
          iconLeft={Icon.Check}
          isSubmitting={submitting}
          onPress={handleSubmit}
        />
      </OfficeHeaderBuilder.Content>
    </OfficeHeader>
  );
};

const getOrganizationSelectorDropdownOptions = ({
  organization,
}: {
  organization: OrganizationModel;
}) => {
  // By default, we want to hide dependent branches from the dropdown
  const organizationOptions = _.filter(organization.company.organizations, (organization) => {
    return OrganizationKind.getIsIndependent(organization.kind);
  });
  return _.sortBy(organizationOptions, ['name']).map((organization) => {
    return {
      label: organization.name,
      value: organization.id,
      organization,
    };
  });
};

const BranchActionPanel = ({
  organization,
  form,
  field,
}: {
  organization: OrganizationModel;
  form: any;
  field: string;
}) => {
  const organizationSelectorDropdownOptions = getOrganizationSelectorDropdownOptions({
    organization,
  });

  return (
    <ActionPanel
      width={800}
      title={'Branch to Transfer'}
      index={0}
      BodyComponent={() => (
        <React.Fragment>
          <Callout text='Projects can only be transferred between the main branch and franchises. For corporate projects, update the dispatch information accordingly.' />
          <Space height={16} />
          <FieldInput
            {...form}
            label={'Branch'}
            component={DropdownInput}
            name={`${field}.assignedToOrganizationId`}
            index={0}
            isRequired
            input={{
              options: organizationSelectorDropdownOptions,
              placeholder: `Select organization`,
              style: {flex: 1},
              setFieldValue: form.setFieldValue,
            }}
          />
        </React.Fragment>
      )}
    />
  );
};

const ProjectTypeActionPanel = ({
  project,
  form,
  field,
}: {
  project: ProjectModel;
  form: any;
  field: string;
}) => {
  return (
    <ActionPanel
      width={800}
      title={'Project Type'}
      index={1}
      BodyComponent={() => (
        <React.Fragment>
          <TransferProjectTypeContent project={project} form={form} field={field} />
        </React.Fragment>
      )}
    />
  );
};

const JobTypesActionPanel = ({
  project,
  form,
  field,
}: {
  project: ProjectModel;
  form: any;
  field: string;
}) => {
  return (
    <ActionPanel
      width={800}
      title={'Job Types'}
      index={2}
      BodyComponent={() => (
        <React.Fragment>
          <EditJobTypesContent project={project} form={form} field={field} />
        </React.Fragment>
      )}
    />
  );
};

const TransferProjectPageContent = ({project}: {project: ProjectModel}) => {
  const field = 'transferProjectToOrganizationForm';
  const {navigator} = useNavigationDOM();
  const errorToast = useToast({
    ToastComponent: ErrorToast,
    message: `Something went wrong. Please try again.`,
  });
  const successToast = useToast({
    ToastComponent: SuccessToast,
    message: `Project transferred successfully!`,
  });

  const transferProjectToOrganizationForm = TransferProjectToOrganizationForm.new({
    projectId: project.id,
  });
  const {form, handleSubmit, submitting} = useTransferProjectMutation({
    transferProjectToOrganizationForm,
    onSuccess: () => {
      successToast.handleToast();
      navigator.goBack();
    },
    onError: (errors) => {
      errorToast.handleToast();
      console.log({errors});
    },
  });

  return (
    <React.Fragment>
      <PageHeader
        project={project}
        handleSubmit={handleSubmit}
        submitting={submitting}
        onClose={() => navigator.goBack()}
      />
      <ContentScrollContainer>
        <BranchActionPanel form={form} organization={project.organization} field={field} />
        <Space height={24} />
        <ProjectTypeActionPanel form={form} project={project} field={field} />
        <Space height={24} />
        <JobTypesActionPanel form={form} project={project} field={field} />
        <Space height={24} />
      </ContentScrollContainer>
    </React.Fragment>
  );
};

const TransferProjectPage = () => {
  const {params, navigator} = useNavigationDOM();
  const {data, loading} = useQuery(TransferProjectPage.query, {
    fetchPolicy: 'network-only',
    variables: {projectUuid: params.projectUuid},
  });

  return (
    <SidebarPageV2 selected={'moves'}>
      <PageContainer>
        <Loading loading={loading} as={() => <LoadingComponent navigator={navigator} />}>
          {() => {
            return <TransferProjectPageContent project={data.project} />;
          }}
        </Loading>
      </PageContainer>
    </SidebarPageV2>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
TransferProjectPage.query = gql`
  ${Project.getName.fragment}
  ${TransferProjectTypeContent.fragment}
  ${EditJobTypesContent.fragment}
  ${UpdateJobTypeForJobForm.edit.fragment}
  ${Job.sortJobsBySequence.fragment}

  query TransferProjectPage($projectUuid: String!) {
    project(uuid: $projectUuid) {
      id
      organization {
        id
        company {
          id
          organizations {
            id
            name
            kind
            isPrimary
          }
        }
      }
      activeJobs {
        id
        ...Job_sortJobsBySequence
        ...UpdateJobTypeForJobForm_edit
      }
      ...Project_getName
      ...TransferProjectTypeContent
      ...EditJobTypesContent
    }
  }
`;

export default TransferProjectPage;
